import type { HostelStatsEntry } from './types';
import type { DashboardStats } from '@/api/types';

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

export interface RentDrillDownSearch {
  hostelId: string;
  status: string;
  from: string;
  to: string;
}

export interface TenantDrillDownSearch {
  hostelId: string;
  status: string;
  joiningDate: string;
  checkoutFrom: string;
  checkoutTo: string;
}

export type RentDrillDownKind = 'OVERDUE_RENT' | 'PENDING_RENT' | 'EXPECTED_TODAY';
export type TenantDrillDownKind = 'CHECKIN_TODAY' | 'CHECKOUT_TOMORROW' | 'STAY_ENDING_SOON' | 'OCCUPIED' | 'RESERVED';

/** Builds the search params for navigating into the Rent page pre-filtered for a dashboard metric. */
export function buildRentSearch(kind: RentDrillDownKind, hostelId: string): RentDrillDownSearch {
  const today = isoDate(new Date());
  switch (kind) {
    case 'OVERDUE_RENT':
      return { hostelId, status: 'OVERDUE', from: '', to: '' };
    case 'PENDING_RENT':
      return { hostelId, status: 'UNPAID', from: '', to: '' };
    case 'EXPECTED_TODAY':
      return { hostelId, status: 'UNPAID', from: today, to: today };
  }
}

/** Builds the search params for navigating into the Tenants page pre-filtered for a dashboard metric. */
export function buildTenantSearch(kind: TenantDrillDownKind, hostelId: string): TenantDrillDownSearch {
  const today = isoDate(new Date());
  const tomorrow = isoDate(new Date(Date.now() + 86_400_000));
  const in10Days = isoDate(new Date(Date.now() + 10 * 86_400_000));
  switch (kind) {
    case 'CHECKIN_TODAY':
      return { hostelId, status: 'ACTIVE', joiningDate: today, checkoutFrom: '', checkoutTo: '' };
    case 'CHECKOUT_TOMORROW':
      return { hostelId, status: 'ACTIVE', joiningDate: '', checkoutFrom: tomorrow, checkoutTo: tomorrow };
    case 'STAY_ENDING_SOON':
      return { hostelId, status: 'ACTIVE', joiningDate: '', checkoutFrom: today, checkoutTo: in10Days };
    case 'OCCUPIED':
      return { hostelId, status: 'ACTIVE', joiningDate: '', checkoutFrom: '', checkoutTo: '' };
    case 'RESERVED':
      return { hostelId, status: 'RESERVED', joiningDate: '', checkoutFrom: '', checkoutTo: '' };
  }
}

/**
 * The Hostel Detail page can only show one hostel at a time. When the dashboard is scoped to a
 * single hostel, use it; otherwise pick the first hostel (among the ones currently loaded) that
 * matches the given bed-count predicate, falling back to the first hostel if none match.
 */
export function pickHostelIdByBedCount(
  scopeHostelId: string,
  perHostelStats: HostelStatsEntry[],
  countSelector: (stats: DashboardStats) => number,
): string {
  if (scopeHostelId) return scopeHostelId;
  const match = perHostelStats.find(p => countSelector(p.stats) > 0);
  return match?.hostel.id ?? perHostelStats[0]?.hostel.id ?? '';
}
